import { type FormEvent, useMemo, useState } from "react";
import { Input, Button, FormField, Checkbox, Wordmark } from "@praesto/ui-kit";
import itLocale from "../locales/it.json";
import enLocale from "../locales/en.json";
import { startSignup, verifySignup, completeSignup, SignupApiError } from "./signup-api";
import { signInToTenant } from "../lib/auth-actions";
import { legalUrl } from "../lib/website-url";
import { RecaptchaNotice } from "../components/RecaptchaNotice";
import { detectLocale, type SignupLocale } from "./detect-locale";

type Locale = SignupLocale;
type Phase = "form" | "otp" | "complete";

const BUNDLES: Record<Locale, Record<string, string>> = { it: itLocale, en: enLocale };

/**
 * Minimal, standalone `t()` for this page only. `SignupPage` renders BEFORE
 * any tenant exists (the whole point of signup), so it deliberately does not
 * go through `core.i18n.t()` like the rest of the app (that requires
 * `useCore()`/`CoreProvider`, which needs a `tenantSlug` to construct a full
 * `CoreAPI` — wrong for a pre-tenant page, and `getCore()` is a
 * process-lifetime singleton that must not be seeded with a fake slug).
 * Reads flat `signup.*` keys straight out of the same locale JSON files the
 * rest of the app uses, with the same `{{param}}` interpolation convention
 * (see `time.relative.minutes` in `locales/it.json`).
 */
function translate(locale: Locale, key: string, params?: Readonly<Record<string, string>>): string {
  const bundle = BUNDLES[locale];
  let value = bundle[key] ?? BUNDLES.en[key] ?? key;
  if (params) {
    for (const [name, v] of Object.entries(params)) {
      value = value.replaceAll(`{{${name}}}`, v);
    }
  }
  return value;
}

function errorMessage(t: (key: string) => string, err: unknown): string {
  if (err instanceof SignupApiError) return t(`signup.error.${err.code}`);
  return t("signup.error.unknown");
}

/**
 * Public, unauthenticated 3-phase self-service signup page:
 * "form" (email + company/structure names + ToS/Privacy consent) →
 * "otp" (email code verification) → "complete" (owner name + password).
 * On success, signs into the brand-new tenant and hands off to it — the
 * onboarding wizard (Task 10) takes over from there.
 *
 * Rendered directly from `App.tsx` (mirrors how `HubView`/`ResetPasswordView`
 * are rendered pre-auth, outside the tenant-scoped TanStack router — see
 * `router.tsx` note below for why `/signup` is NOT registered there).
 */
export function SignupPage() {
  const locale = useMemo(() => detectLocale(), []);
  const t = useMemo(
    () => (key: string, params?: Readonly<Record<string, string>>) =>
      translate(locale, key, params),
    [locale],
  );

  const [phase, setPhase] = useState<Phase>("form");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [structureName, setStructureName] = useState("");
  const [consent, setConsent] = useState(false);
  const [signupId, setSignupId] = useState("");
  const [code, setCode] = useState("");
  const [ownerDisplayName, setOwnerDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitForm(e: FormEvent) {
    e.preventDefault();
    if (!consent) return;
    setBusy(true);
    setError(null);
    try {
      const { signupId: id } = await startSignup({
        email,
        companyName,
        structureName,
        locale,
        consent: { tos: true, privacy: true },
      });
      setSignupId(id);
      setPhase("otp");
    } catch (err) {
      setError(errorMessage(t, err));
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifySignup({ signupId, code });
      setPhase("complete");
    } catch (err) {
      setError(errorMessage(t, err));
    } finally {
      setBusy(false);
    }
  }

  async function submitComplete(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      setError(t("signup.error.password_too_short"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { tenantId, customToken } = await completeSignup({
        signupId,
        ownerDisplayName,
        password,
      });
      try {
        await signInToTenant(tenantId, customToken);
      } catch {
        setError(t("signup.error.signin_failed"));
        setBusy(false);
        return;
      }
      // Full navigation (not an SPA route change — there is no router mounted
      // pre-auth). Go STRAIGHT to the mandatory wizard: a fresh self-service
      // tenant is always `onboarding: pending`, so landing on the tenant root
      // would only flash Home for a beat before `OnboardingGuard` redirects
      // here anyway. Navigating directly avoids that flash.
      window.location.assign(`/${tenantId}/onboarding`);
    } catch (err) {
      setError(errorMessage(t, err));
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-default flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {/* The brand arrives as a file (C3F v18) — the mono "Praesto" eyebrow
            was the pre-v18 identity. */}
        <Wordmark height={34} />

        {phase === "form" && (
          <form onSubmit={submitForm}>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">{t("signup.title")}</h1>
            <p className="mt-3 text-sm text-fg-muted leading-relaxed">{t("signup.subtitle")}</p>
            <div className="mt-6 space-y-4">
              <FormField label={t("signup.field.email")}>
                <Input
                  id="signup-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormField>
              <FormField label={t("signup.field.companyName")}>
                <Input
                  id="signup-company-name"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </FormField>
              <FormField label={t("signup.field.structureName")}>
                <Input
                  id="signup-structure-name"
                  required
                  value={structureName}
                  onChange={(e) => setStructureName(e.target.value)}
                />
              </FormField>
            </div>
            <div className="mt-6">
              <Checkbox
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                label={
                  <span>
                    {t("signup.consent.text_prefix")}{" "}
                    <a
                      href={legalUrl(locale, "/legal/terms")}
                      className="underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("signup.consent.tos_link_text")}
                    </a>{" "}
                    {t("signup.consent.text_middle")}{" "}
                    <a
                      href={legalUrl(locale, "/privacy")}
                      className="underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("signup.consent.privacy_link_text")}
                    </a>
                  </span>
                }
              />
            </div>
            {error && (
              <p className="mt-4 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy || !consent}
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
            >
              {busy ? t("signup.submit.busy") : t("signup.submit")}
            </Button>
          </form>
        )}

        {phase === "otp" && (
          <form onSubmit={submitOtp}>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">{t("signup.otp.title")}</h1>
            <p className="mt-3 text-sm text-fg-muted leading-relaxed">
              {t("signup.otp.subtitle", { email })}
            </p>
            <div className="mt-6">
              <FormField label={t("signup.otp.field.code")}>
                <Input
                  id="signup-otp-code"
                  inputMode="numeric"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </FormField>
            </div>
            {error && (
              <p className="mt-4 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
            >
              {busy ? t("signup.otp.submit.busy") : t("signup.otp.submit")}
            </Button>
          </form>
        )}

        {phase === "complete" && (
          <form onSubmit={submitComplete}>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">
              {t("signup.complete.title")}
            </h1>
            <p className="mt-3 text-sm text-fg-muted leading-relaxed">
              {t("signup.complete.subtitle")}
            </p>
            <div className="mt-6 space-y-4">
              <FormField label={t("signup.complete.field.ownerDisplayName")}>
                <Input
                  id="signup-owner-name"
                  autoComplete="name"
                  required
                  value={ownerDisplayName}
                  onChange={(e) => setOwnerDisplayName(e.target.value)}
                />
              </FormField>
              <FormField
                label={t("signup.complete.field.password")}
                helper={t("signup.complete.field.password_helper")}
              >
                <Input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormField>
            </div>
            {error && (
              <p className="mt-4 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
            >
              {busy ? t("signup.complete.submit.busy") : t("signup.complete.submit")}
            </Button>
          </form>
        )}

        {/* Every phase posts through signup-api with an App Check token, so the
            notice stays under all three forms. */}
        <RecaptchaNotice
          text={t("signup.recaptcha.text")}
          linkText={t("signup.recaptcha.link_text")}
          href={legalUrl(locale, "/privacy#recaptcha")}
        />
      </div>
    </div>
  );
}
